import Button from './Button'

/**
 * ActionsColumn Component
 * Renders action buttons (Edit, Save, Cancel, Approve, Reject) for a table row
 *
 * @param {Object} props
 * @param {Object} props.report - Report/form data object
 * @param {boolean} props.isEditing - Whether this row is in edit mode
 * @param {boolean} props.isLoading - Whether an action is in progress
 * @param {string} props.currentAction - Current action being performed ('approve', 'reject', 'save')
 * @param {boolean} props.isAnyRowEditing - Whether any row in the table is being edited
 * @param {boolean} props.isAnyCheckboxSelected - Whether any checkbox is selected for bulk operations
 * @param {boolean} props.isThisRowSelected - Whether this specific row's checkbox is selected
 * @param {number} props.selectedCount - Number of selected forms
 * @param {boolean} props.showEditButton - Whether to show the Edit button (default: true)
 * @param {Object} props.permissions - Permission object with canEdit, canApprove, canReject flags
 * @param {Function} props.onSave - Handler for save action
 * @param {Function} props.onCancel - Handler for cancel action
 * @param {Function} props.onEdit - Handler for edit action
 * @param {Function} props.onApprove - Handler for approve action
 * @param {Function} props.onReject - Handler for reject action
 */
const ActionsColumn = ({
  report,
  isEditing = false,
  isLoading = false,
  currentAction = null,
  isAnyRowEditing = false,
  isAnyCheckboxSelected = false,
  isThisRowSelected = false,
  selectedCount = 0,
  showEditButton = true,
  permissions = null, // { canEdit, canApprove, canReject, hasReviewerAccess }
  onSave,
  onCancel,
  onEdit,
  onApprove,
  onReject,
}) => {
  // Disable buttons when:
  // - Multiple forms selected (selectedCount > 1) - all actions disabled, use bulk operations
  // - Single form selected but this row is NOT the selected one - only the selected form can act
  // Enable ONLY when: no checkbox selected OR this form is the only one selected
  const disabledDueToSelection =
    (selectedCount > 1) ||                    // Multiple selected - disable all
    (selectedCount === 1 && !isThisRowSelected) // One selected but not this one - disable this

  // Permission checks (default to true if permissions not provided for backward compatibility)
  const canEdit = permissions?.canEdit ?? true
  const canApprove = permissions?.canApprove ?? true
  const canReject = permissions?.canReject ?? true
  const hasReviewerAccess = permissions?.hasReviewerAccess ?? true
  return (
    <td
      style={{
        position: 'sticky',
        right: 0,
        backgroundColor: '#fff',
        boxShadow: '-2px 0 4px rgba(0, 0, 0, 0.05)',
        zIndex: 1,
        whiteSpace: 'nowrap',
        minWidth: '310px',
        width: '310px',
        padding: '0.65rem 0.75rem',
      }}
    >
      <div className="as-action-row">
        {isEditing ? (
          <>
            <Button
              variant="green"
              icon={isLoading ? '⏳' : '✓'}
              onClick={() => onSave && onSave(report)}
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : 'Save'}
            </Button>

            <Button
              variant="gray"
              icon="✕"
              onClick={() => onCancel && onCancel()}
              disabled={isLoading}
            >
              Cancel
            </Button>
          </>
        ) : (
          <>
            {hasReviewerAccess ? (
              <>
                {showEditButton && canEdit && (
                  <Button
                    variant="purple"
                    icon="✎"
                    onClick={() => onEdit && onEdit(report)}
                    disabled={isAnyRowEditing || disabledDueToSelection}
                  >
                    Edit
                  </Button>
                )}

                {canApprove && (
                  <Button
                    variant="blue"
                    icon={isLoading && currentAction === 'approve' ? '⏳' : '✓'}
                    onClick={() => onApprove && onApprove(report)}
                    disabled={isLoading || isAnyRowEditing || disabledDueToSelection}
                    aria-label={`Approve form ${report.formNum || report.id}`}
                  >
                    {isLoading && currentAction === 'approve' ? 'Approving...' : 'Approve'}
                  </Button>
                )}

                {canReject && (
                  <Button
                    variant="red"
                    icon={isLoading && currentAction === 'reject' ? '⏳' : '✕'}
                    onClick={() => onReject && onReject(report)}
                    disabled={isLoading || isAnyRowEditing || disabledDueToSelection}
                    aria-label={`Reject form ${report.formNum || report.id}`}
                  >
                    {isLoading && currentAction === 'reject' ? 'Rejecting...' : 'Reject'}
                  </Button>
                )}
              </>
            ) : (
              <span style={{ color: '#6c757d', fontSize: '0.85rem', fontStyle: 'italic' }}>
                View Only
              </span>
            )}
          </>
        )}
      </div>
    </td>
  )
}

export default ActionsColumn
